import { useCallback, useMemo, useRef, useState } from 'react'

import { useAction } from '../components/toast'
import { Badge, Card, Kpi, Loaded, Modal, Notice, Page, Table } from '../components/ui'
import { del, get, patch, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { dateTime, money, ms, relative, tokens } from '../lib/format'
import { useResource } from '../lib/useResource'

// The canvas stores ReactFlow-shaped nodes and edges, so the editor can be
// swapped for ReactFlow later without touching the API or migrating a row.
type NodeData = { label?: string; agent_key?: string; instruction?: string }
type FlowNode = { id: string; type?: string; position: { x: number; y: number }; data: NodeData }
type FlowEdge = { id: string; source: string; target: string }

type Agent = {
  id: string
  key: string
  name: string
  description: string
  system_prompt: string
  model_id: string
  task_class: string
  max_output_tokens: number
  enabled: boolean
  updated_at: string | null
}

type Graph = {
  id: string
  key: string
  name: string
  description: string
  nodes: FlowNode[]
  edges: FlowEdge[]
  version: number
  enabled: boolean
  trigger: string
  trigger_config: Record<string, unknown>
  updated_at: string | null
  /** Set when the canvas cannot run — a loop, or a step with no agent. */
  problem: string
}

type Step = {
  sequence: number
  node_id: string
  agent_key: string
  status: string
  model_id: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  latency_ms: number
  error: string
  started_at: string | null
  /** Null unless you hold ai:prompt:read — these quote someone's vitals. */
  prompt: string | null
  output: string | null
}

type Run = {
  id: string
  graph_id: string
  graph_key: string
  graph_name: string
  graph_version: number
  subject_user_id: string | null
  subject_email: string | null
  trigger: string
  status: string
  started_at: string | null
  finished_at: string | null
  duration_ms: number | null
  total_cost_usd: number
  total_tokens: number
  error: string
  step_count: number
}

type RunDetail = Run & { output: Record<string, string>; steps: Step[] }

type Summary = {
  window_hours: number
  total: number
  succeeded: number
  failed: number
  halted: number
  running: number
  cost_usd: number
  tokens: number
  p50_duration_ms: number
  slowest_ms: number
}

const TASK_CLASSES = ['chat', 'triage', 'explain', 'summarize', 'report', 'vision']

function statusTone(status: string): 'green' | 'alert' | 'orange' | 'cyan' | undefined {
  if (status === 'succeeded') return 'green'
  if (status === 'failed') return 'alert'
  if (status === 'halted') return 'orange'
  if (status === 'running') return 'cyan'
  return undefined
}

const NODE_W = 190
const NODE_H = 78

// ── canvas ───────────────────────────────────────────────────────────────────

/**
 * The flow, drawn.
 *
 * Nodes are absolutely positioned and dragged with pointer events; edges are one
 * SVG layer underneath. Deliberately not a graph library: this console has no
 * bundler access to add one right now, and the stored shape is ReactFlow's, so
 * dropping ReactFlow in later replaces this file and nothing else.
 */
function Canvas({
  nodes,
  edges,
  selected,
  linkFrom,
  onSelect,
  onMove,
  onLink,
  readOnly,
}: {
  nodes: FlowNode[]
  edges: FlowEdge[]
  selected: string | null
  linkFrom: string | null
  onSelect: (id: string | null) => void
  onMove: (id: string, x: number, y: number) => void
  onLink: (target: string) => void
  readOnly: boolean
}) {
  const surface = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{ id: string; dx: number; dy: number } | null>(null)

  const centre = (node: FlowNode) => ({
    x: node.position.x + NODE_W / 2,
    y: node.position.y + NODE_H / 2,
  })

  const onPointerDown = (event: React.PointerEvent, node: FlowNode) => {
    onSelect(node.id)
    if (readOnly) return
    const box = surface.current?.getBoundingClientRect()
    if (!box) return
    drag.current = {
      id: node.id,
      dx: event.clientX - box.left - node.position.x,
      dy: event.clientY - box.top - node.position.y,
    }
    ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent) => {
    const held = drag.current
    const box = surface.current?.getBoundingClientRect()
    if (!held || !box) return
    // Clamped so a node can never be dragged out of reach of the pointer.
    const x = Math.max(0, Math.round(event.clientX - box.left - held.dx))
    const y = Math.max(0, Math.round(event.clientY - box.top - held.dy))
    onMove(held.id, x, y)
  }

  const endDrag = () => {
    drag.current = null
  }

  const extent = useMemo(() => {
    const right = Math.max(900, ...nodes.map((n) => n.position.x + NODE_W + 60))
    const bottom = Math.max(360, ...nodes.map((n) => n.position.y + NODE_H + 60))
    return { right, bottom }
  }, [nodes])

  return (
    <div
      ref={surface}
      className="flow-canvas"
      style={{ height: extent.bottom, minWidth: extent.right }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onClick={(event) => {
        if (event.target === surface.current) onSelect(null)
      }}
    >
      <svg className="flow-canvas__wires" width={extent.right} height={extent.bottom}>
        <defs>
          <marker
            id="flow-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
          </marker>
        </defs>
        {edges.map((edge) => {
          const from = nodes.find((n) => n.id === edge.source)
          const to = nodes.find((n) => n.id === edge.target)
          if (!from || !to) return null
          const a = centre(from)
          const b = centre(to)
          return (
            <line
              key={edge.id}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              className="flow-wire"
              markerEnd="url(#flow-arrow)"
            />
          )
        })}
      </svg>

      {nodes.map((node, index) => {
        const classes = ['flow-node']
        if (node.id === selected) classes.push('flow-node--selected')
        if (node.id === linkFrom) classes.push('flow-node--linking')
        if (!node.data.agent_key) classes.push('flow-node--incomplete')
        return (
          <div
            key={node.id}
            className={classes.join(' ')}
            style={{ left: node.position.x, top: node.position.y, width: NODE_W }}
            onPointerDown={(event) => onPointerDown(event, node)}
            onClick={() => {
              if (linkFrom && linkFrom !== node.id) onLink(node.id)
            }}
          >
            <div className="flow-node__step">Step {index + 1}</div>
            <div className="flow-node__label">{node.data.label || node.id}</div>
            <div className="flow-node__agent">
              {node.data.agent_key || 'no agent assigned'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── the flow editor ──────────────────────────────────────────────────────────

function Editor({
  graph,
  agents,
  onClose,
  onSaved,
}: {
  graph: Graph
  agents: Agent[]
  onClose: () => void
  onSaved: () => void
}) {
  const { can } = useAuth()
  const run = useAction()
  const editable = can(P.aiAgentWrite)
  const runnable = can(P.aiAgentRun)

  const [nodes, setNodes] = useState<FlowNode[]>(graph.nodes ?? [])
  const [edges, setEdges] = useState<FlowEdge[]>(graph.edges ?? [])
  const [selected, setSelected] = useState<string | null>(null)
  const [linkFrom, setLinkFrom] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lastRun, setLastRun] = useState<RunDetail | null>(null)
  const [seedInput, setSeedInput] = useState('')

  const dirty =
    JSON.stringify(nodes) !== JSON.stringify(graph.nodes ?? []) ||
    JSON.stringify(edges) !== JSON.stringify(graph.edges ?? [])

  const node = nodes.find((n) => n.id === selected) ?? null

  // The same rules the API enforces, checked here so the editor can say what is
  // wrong while it is being drawn rather than at the first run.
  const problem = useMemo(() => {
    if (nodes.length === 0) return 'This flow has no steps in it yet.'
    const missing = nodes.filter((n) => !n.data.agent_key).length
    if (missing > 0) return `${missing} step(s) have no agent assigned.`
    if (hasCycle(nodes, edges)) return 'The flow has a loop in it, so it cannot be run.'
    return ''
  }, [nodes, edges])

  const addNode = () => {
    const id = `step_${Date.now().toString(36)}`
    const column = nodes.length % 4
    const row = Math.floor(nodes.length / 4)
    setNodes([
      ...nodes,
      {
        id,
        type: 'agent',
        position: { x: 40 + column * 230, y: 40 + row * 130 },
        data: { label: 'New step', agent_key: '', instruction: '' },
      },
    ])
    setSelected(id)
  }

  const updateNode = (id: string, data: Partial<NodeData>) => {
    setNodes(nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...data } } : n)))
  }

  const removeNode = (id: string) => {
    setNodes(nodes.filter((n) => n.id !== id))
    // Edges touching a removed node go with it, or the canvas keeps drawing
    // wires to nothing.
    setEdges(edges.filter((e) => e.source !== id && e.target !== id))
    setSelected(null)
  }

  const moveNode = useCallback((id: string, x: number, y: number) => {
    setNodes((current) =>
      current.map((n) => (n.id === id ? { ...n, position: { x, y } } : n)),
    )
  }, [])

  const linkTo = (target: string) => {
    if (!linkFrom) return
    const exists = edges.some((e) => e.source === linkFrom && e.target === target)
    const reverse = edges.some((e) => e.source === target && e.target === linkFrom)
    setLinkFrom(null)
    if (exists || reverse) return
    setEdges([...edges, { id: `e_${linkFrom}_${target}`, source: linkFrom, target }])
  }

  const save = async () => {
    setBusy(true)
    await run(
      () => patch(`/v1/admin/agents/graphs/${graph.id}`, { nodes, edges }),
      'Flow saved. Runs from now on record this version.',
      onSaved,
    )
    setBusy(false)
  }

  const runNow = async () => {
    setBusy(true)
    await run(
      async () => {
        const detail = await post<RunDetail>(
          `/v1/admin/agents/graphs/${graph.id}/runs`,
          { input: seedInput },
        )
        setLastRun(detail)
      },
      'Flow finished. The step log is below.',
      onSaved,
    )
    setBusy(false)
  }

  return (
    <Modal title={`${graph.name} · v${graph.version}`} onClose={onClose} wide>
      <div className="stack">
        {problem ? (
          <Notice tone="warn">{problem}</Notice>
        ) : (
          <Notice>
            Steps run left to right along the arrows. Each one is handed what the
            steps feeding it produced.
          </Notice>
        )}

        {editable && (
          <div className="row row--wrap">
            <button className="btn btn--sm" onClick={addNode}>
              Add step
            </button>
            <button
              className="btn btn--sm"
              disabled={!selected}
              onClick={() => setLinkFrom(selected)}
              title="Then click the step it should feed into"
            >
              {linkFrom ? 'Now click the next step…' : 'Connect from selected'}
            </button>
            {linkFrom && (
              <button className="btn btn--sm btn--ghost" onClick={() => setLinkFrom(null)}>
                Cancel
              </button>
            )}
            <div className="spacer" />
            <button
              className="btn btn--sm btn--primary"
              disabled={busy || !dirty}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : dirty ? 'Save flow' : 'Saved'}
            </button>
          </div>
        )}

        <div className="flow-canvas__scroll">
          <Canvas
            nodes={nodes}
            edges={edges}
            selected={selected}
            linkFrom={linkFrom}
            onSelect={setSelected}
            onMove={moveNode}
            onLink={linkTo}
            readOnly={!editable}
          />
        </div>

        {node && (
          <Card title="Step properties" hint={node.id}>
            <div className="stack" style={{ padding: 16 }}>
              <div className="field">
                <label className="field__label" htmlFor="node-label">
                  Name
                </label>
                <input
                  id="node-label"
                  className="input"
                  value={node.data.label ?? ''}
                  disabled={!editable}
                  onChange={(event) => updateNode(node.id, { label: event.target.value })}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="node-agent">
                  Agent
                </label>
                <select
                  id="node-agent"
                  className="input"
                  value={node.data.agent_key ?? ''}
                  disabled={!editable}
                  onChange={(event) => updateNode(node.id, { agent_key: event.target.value })}
                >
                  <option value="">— choose an agent —</option>
                  {agents.map((agent) => (
                    <option key={agent.key} value={agent.key} disabled={!agent.enabled}>
                      {agent.name}
                      {agent.enabled ? '' : ' (turned off)'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="field__label" htmlFor="node-instruction">
                  Instruction for this step
                </label>
                <textarea
                  id="node-instruction"
                  className="input"
                  rows={3}
                  value={node.data.instruction ?? ''}
                  disabled={!editable}
                  onChange={(event) =>
                    updateNode(node.id, { instruction: event.target.value })
                  }
                />
              </div>
              {editable && (
                <div className="row row--end">
                  <button className="btn btn--sm btn--ghost" onClick={() => removeNode(node.id)}>
                    Remove step
                  </button>
                </div>
              )}
            </div>
          </Card>
        )}

        {runnable && (
          <Card
            title="Try it"
            hint="Runs immediately against whatever you type here, and records the result."
          >
            <div className="stack" style={{ padding: 16 }}>
              {dirty && (
                <Notice tone="warn">
                  Unsaved changes run as the last saved version. Save first to try what
                  is on the canvas.
                </Notice>
              )}
              <textarea
                className="input"
                rows={3}
                placeholder="Readings to feed the first step, e.g. resting hr 41 overnight, spo2 96"
                value={seedInput}
                onChange={(event) => setSeedInput(event.target.value)}
              />
              <div className="row row--end">
                <button
                  className="btn btn--sm btn--primary"
                  disabled={busy || problem !== ''}
                  onClick={() => void runNow()}
                >
                  {busy ? 'Running…' : 'Run now'}
                </button>
              </div>
              {lastRun && <StepLog run={lastRun} />}
            </div>
          </Card>
        )}
      </div>
    </Modal>
  )
}

/** Depth-first cycle check, mirroring the server's topological sort. */
function hasCycle(nodes: FlowNode[], edges: FlowEdge[]): boolean {
  const out = new Map<string, string[]>()
  nodes.forEach((n) => out.set(n.id, []))
  edges.forEach((e) => {
    if (out.has(e.source) && out.has(e.target) && e.source !== e.target) {
      out.get(e.source)?.push(e.target)
    }
  })
  const state = new Map<string, number>() // 1 = in progress, 2 = done
  const walk = (id: string): boolean => {
    const mark = state.get(id)
    if (mark === 1) return true
    if (mark === 2) return false
    state.set(id, 1)
    for (const next of out.get(id) ?? []) {
      if (walk(next)) return true
    }
    state.set(id, 2)
    return false
  }
  return nodes.some((n) => walk(n.id))
}

// ── run log ──────────────────────────────────────────────────────────────────

function StepLog({ run }: { run: RunDetail }) {
  return (
    <div className="stack">
      <div className="row row--wrap">
        <Badge tone={statusTone(run.status)}>{run.status}</Badge>
        <span className="dim">
          {run.step_count} steps · {money(run.total_cost_usd, 4)} · {tokens(run.total_tokens)}{' '}
          tokens · {ms(run.duration_ms)}
        </span>
      </div>
      {run.error && <Notice tone="warn">{run.error}</Notice>}
      {run.steps.map((step) => (
        <Card key={step.sequence} title={`${step.sequence + 1}. ${step.agent_key}`}>
          <div className="stack" style={{ padding: 14 }}>
            <div className="row row--wrap">
              <Badge tone={statusTone(step.status)}>{step.status}</Badge>
              <span className="dim">
                {step.model_id || 'no model'} · {ms(step.latency_ms)} ·{' '}
                {money(step.cost_usd, 4)}
              </span>
            </div>
            {step.error && <Notice tone="warn">{step.error}</Notice>}
            {step.prompt === null ? (
              <p className="muted" style={{ margin: 0 }}>
                The prompt and reply are withheld — they quote this person's health
                readings, which needs <span className="mono">ai:prompt:read</span>.
              </p>
            ) : (
              <>
                <div className="field">
                  <div className="field__label">Asked</div>
                  <pre className="pre">{step.prompt}</pre>
                </div>
                <div className="field">
                  <div className="field__label">Replied</div>
                  <pre className="pre">{step.output}</pre>
                </div>
              </>
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}

function RunInspector({ runId, onClose }: { runId: string; onClose: () => void }) {
  const detail = useResource(
    (signal) => get<RunDetail>(`/v1/admin/agents/runs/${runId}`, undefined, signal),
    [runId],
  )
  return (
    <Modal title="Run" onClose={onClose} wide>
      <div style={{ padding: 4 }}>
        <Loaded resource={detail}>{(data) => <StepLog run={data} />}</Loaded>
      </div>
    </Modal>
  )
}

// ── agent editor ─────────────────────────────────────────────────────────────

function AgentEditor({
  agent,
  onClose,
  onSaved,
}: {
  agent: Agent | null
  onClose: () => void
  onSaved: () => void
}) {
  const run = useAction()
  const creating = agent === null
  const [draft, setDraft] = useState<Partial<Agent>>(
    agent ?? {
      key: '',
      name: '',
      description: '',
      system_prompt: '',
      model_id: '',
      task_class: 'chat',
      max_output_tokens: 1024,
      enabled: true,
    },
  )
  const [busy, setBusy] = useState(false)

  const set = (fields: Partial<Agent>) =>
    setDraft((current) => ({ ...current, ...fields }))

  // The server enforces this too; saying so here saves a round trip that would
  // come back as a validation error with no field attached to it.
  const keyOk = creating ? /^[a-z0-9][a-z0-9_-]{1,63}$/.test(draft.key ?? '') : true
  const canSave = (draft.name ?? '').trim().length > 0 && keyOk

  const save = async () => {
    setBusy(true)
    await run(
      () =>
        agent === null
          ? post('/v1/admin/agents', draft)
          : patch(`/v1/admin/agents/${agent.id}`, {
              name: draft.name,
              description: draft.description,
              system_prompt: draft.system_prompt,
              model_id: draft.model_id,
              task_class: draft.task_class,
              max_output_tokens: draft.max_output_tokens,
              enabled: draft.enabled,
            }),
      creating ? 'Agent created.' : 'Agent saved.',
      () => {
        onSaved()
        onClose()
      },
    )
    setBusy(false)
  }

  return (
    <Modal title={agent === null ? 'New agent' : agent.name} onClose={onClose} wide>
      <div className="stack">
        {creating && (
          <div className="field">
            <label className="field__label" htmlFor="agent-key">
              Handle (lowercase, cannot be changed later)
            </label>
            <input
              id="agent-key"
              className="input mono"
              value={draft.key ?? ''}
              placeholder="risk_triage"
              onChange={(event) => set({ key: event.target.value })}
            />
            {!keyOk && (draft.key ?? '') !== '' && (
              <p className="muted" style={{ margin: '4px 0 0' }}>
                Lowercase letters, numbers, dashes and underscores only.
              </p>
            )}
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Flows refer to an agent by its handle, so renaming the display name later
              never breaks a flow.
            </p>
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="agent-name">
            Name
          </label>
          <input
            id="agent-name"
            className="input"
            value={draft.name ?? ''}
            onChange={(event) => set({ name: event.target.value })}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="agent-desc">
            What it is for
          </label>
          <input
            id="agent-desc"
            className="input"
            value={draft.description ?? ''}
            onChange={(event) => set({ description: event.target.value })}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="agent-prompt">
            System prompt
          </label>
          <textarea
            id="agent-prompt"
            className="input"
            rows={12}
            value={draft.system_prompt ?? ''}
            onChange={(event) => set({ system_prompt: event.target.value })}
          />
          <p className="muted" style={{ margin: '4px 0 0' }}>
            This is the agent's behaviour. Changing it changes what every alert it
            writes says, and the change is recorded in the audit log.
          </p>
        </div>

        <div className="row row--wrap">
          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <label className="field__label" htmlFor="agent-task">
              Routing hint
            </label>
            <select
              id="agent-task"
              className="input"
              value={draft.task_class ?? 'chat'}
              onChange={(event) => set({ task_class: event.target.value })}
            >
              {TASK_CLASSES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: 1, minWidth: 180 }}>
            <label className="field__label" htmlFor="agent-model">
              Pin a model (optional)
            </label>
            <input
              id="agent-model"
              className="input mono"
              placeholder="leave blank to let routing choose"
              value={draft.model_id ?? ''}
              onChange={(event) => set({ model_id: event.target.value })}
            />
          </div>
          <div className="field" style={{ width: 150 }}>
            <label className="field__label" htmlFor="agent-tokens">
              Max reply tokens
            </label>
            <input
              id="agent-tokens"
              className="input"
              type="number"
              min={64}
              max={32000}
              value={draft.max_output_tokens ?? 1024}
              onChange={(event) => set({ max_output_tokens: Number(event.target.value) })}
            />
          </div>
        </div>

        <label className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={draft.enabled ?? true}
            onChange={(event) => set({ enabled: event.target.checked })}
          />
          <span>Enabled — a turned-off agent stops any flow that uses it</span>
        </label>

        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={busy || !canSave}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : creating ? 'Create agent' : 'Save agent'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── screen ───────────────────────────────────────────────────────────────────

export function AgentFlows() {
  const { can } = useAuth()
  const action = useAction()
  const editable = can(P.aiAgentWrite)

  const agents = useResource((signal) => get<Agent[]>('/v1/admin/agents', undefined, signal))
  const graphs = useResource((signal) =>
    get<Graph[]>('/v1/admin/agents/graphs', undefined, signal),
  )
  const runs = useResource((signal) =>
    get<Run[]>('/v1/admin/agents/runs', { limit: 50 }, signal),
  )
  const summary = useResource((signal) =>
    get<Summary>('/v1/admin/agents/runs-summary', { hours: 24 }, signal),
  )

  const [editing, setEditing] = useState<Graph | null>(null)
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [creatingAgent, setCreatingAgent] = useState(false)
  const [creatingFlow, setCreatingFlow] = useState(false)
  const [inspecting, setInspecting] = useState<string | null>(null)

  const reloadAll = () => {
    graphs.reload()
    runs.reload()
    summary.reload()
  }

  const seed = () =>
    void action(
      () => post('/v1/admin/agents/seed'),
      'Starter agents and flow installed. Nothing existing was changed.',
      () => {
        agents.reload()
        reloadAll()
      },
    )

  const removeAgent = (agent: Agent) =>
    void action(() => del(`/v1/admin/agents/${agent.id}`), 'Agent deleted.', agents.reload)

  const removeGraph = (graph: Graph) =>
    void action(() => del(`/v1/admin/agents/graphs/${graph.id}`), 'Flow deleted.', reloadAll)

  return (
    <Page
      title="Agent flows"
      subtitle="Agents, the flows built from them, and every run they have made."
      actions={
        editable && (
          <>
            <button className="btn btn--sm" onClick={seed}>
              Install starter flow
            </button>
            <button className="btn btn--sm" onClick={() => setCreatingAgent(true)}>
              New agent
            </button>
            <button className="btn btn--sm btn--primary" onClick={() => setCreatingFlow(true)}>
              New flow
            </button>
          </>
        )
      }
    >
      <div className="stack">
        <Loaded resource={summary}>
          {(data) => (
            <div className="grid grid--kpi">
              <Kpi label="Runs (24h)" value={`${data.total}`} />
              <Kpi label="Succeeded" value={`${data.succeeded}`} tone="green" />
              <Kpi
                label="Failed"
                value={`${data.failed}`}
                tone={data.failed > 0 ? 'alert' : undefined}
                foot={data.failed > 0 ? 'Open a run below to see which step.' : undefined}
              />
              <Kpi
                label="Halted"
                value={`${data.halted}`}
                tone={data.halted > 0 ? 'orange' : undefined}
                foot={data.halted > 0 ? 'Stopped on budget, not by an error.' : undefined}
              />
              <Kpi label="Spend (24h)" value={money(data.cost_usd, 4)} />
              <Kpi label="Median run" value={ms(data.p50_duration_ms)} />
            </div>
          )}
        </Loaded>

        <Card
          title="Flows"
          hint="A flow runs its steps in order, each one handed what the step before produced."
        >
          <Loaded
            resource={graphs}
            emptyWhen={(rows) => rows.length === 0}
            empty={
              editable
                ? 'No flows yet. "Install starter flow" creates a working daily health check you can edit.'
                : 'No flows have been created yet.'
            }
          >
            {(rows) => (
              <Table
                columns={[
                  { key: 'name', header: 'Flow', strong: true, render: (row) => row.name },
                  {
                    key: 'steps',
                    header: 'Steps',
                    render: (row) => `${(row.nodes ?? []).length}`,
                  },
                  { key: 'version', header: 'Version', render: (row) => `v${row.version}` },
                  { key: 'trigger', header: 'Runs on', render: (row) => row.trigger },
                  {
                    key: 'state',
                    header: 'State',
                    render: (row) =>
                      row.problem ? (
                        <Badge tone="orange">{row.problem}</Badge>
                      ) : row.enabled ? (
                        <Badge tone="green">ready</Badge>
                      ) : (
                        <Badge>draft</Badge>
                      ),
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: (row) => (
                      <div className="row">
                        <button className="btn btn--sm" onClick={() => setEditing(row)}>
                          {editable ? 'Edit' : 'View'}
                        </button>
                        {editable && (
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={() => removeGraph(row)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    ),
                  },
                ]}
                rows={rows}
                rowKey={(row) => row.id}
              />
            )}
          </Loaded>
        </Card>

        <Card
          title="Agents"
          hint="Each one is a named role with its own prompt. Flows are built from these."
        >
          <Loaded
            resource={agents}
            emptyWhen={(rows) => rows.length === 0}
            empty="No agents yet."
          >
            {(rows) => (
              <Table
                columns={[
                  { key: 'name', header: 'Agent', strong: true, render: (row) => row.name },
                  {
                    key: 'key',
                    header: 'Handle',
                    render: (row) => <span className="mono">{row.key}</span>,
                  },
                  { key: 'task', header: 'Routing', render: (row) => row.task_class },
                  {
                    key: 'model',
                    header: 'Model',
                    render: (row) => row.model_id || <span className="dim">routed</span>,
                  },
                  {
                    key: 'enabled',
                    header: '',
                    render: (row) =>
                      row.enabled ? <Badge tone="green">on</Badge> : <Badge tone="orange">off</Badge>,
                  },
                  {
                    key: 'actions',
                    header: '',
                    render: (row) =>
                      editable ? (
                        <div className="row">
                          <button className="btn btn--sm" onClick={() => setEditingAgent(row)}>
                            Edit
                          </button>
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={() => removeAgent(row)}
                          >
                            Delete
                          </button>
                        </div>
                      ) : null,
                  },
                ]}
                rows={rows}
                rowKey={(row) => row.id}
              />
            )}
          </Loaded>
        </Card>

        <Card title="Recent runs" hint="Newest first. Open one to see what each step did.">
          <Loaded
            resource={runs}
            emptyWhen={(rows) => rows.length === 0}
            empty="Nothing has run yet."
          >
            {(rows) => (
              <Table
                columns={[
                  { key: 'flow', header: 'Flow', strong: true, render: (row) => row.graph_name },
                  {
                    key: 'when',
                    header: 'Started',
                    render: (row) => (
                      <span title={dateTime(row.started_at)}>{relative(row.started_at)}</span>
                    ),
                  },
                  {
                    key: 'subject',
                    header: 'For',
                    render: (row) => row.subject_email ?? <span className="dim">—</span>,
                  },
                  {
                    key: 'status',
                    header: 'Result',
                    render: (row) => <Badge tone={statusTone(row.status)}>{row.status}</Badge>,
                  },
                  { key: 'steps', header: 'Steps', render: (row) => `${row.step_count}` },
                  { key: 'took', header: 'Took', render: (row) => ms(row.duration_ms) },
                  { key: 'cost', header: 'Cost', render: (row) => money(row.total_cost_usd, 4) },
                ]}
                rows={rows}
                rowKey={(row) => row.id}
                onRowClick={(row) => setInspecting(row.id)}
              />
            )}
          </Loaded>
        </Card>
      </div>

      {editing && (
        <Editor
          graph={editing}
          agents={agents.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={reloadAll}
        />
      )}
      {(creatingAgent || editingAgent) && (
        <AgentEditor
          agent={editingAgent}
          onClose={() => {
            setCreatingAgent(false)
            setEditingAgent(null)
          }}
          onSaved={agents.reload}
        />
      )}
      {creatingFlow && (
        <NewFlow
          onClose={() => setCreatingFlow(false)}
          onCreated={(graph) => {
            setCreatingFlow(false)
            reloadAll()
            setEditing(graph)
          }}
        />
      )}
      {inspecting && <RunInspector runId={inspecting} onClose={() => setInspecting(null)} />}
    </Page>
  )
}

function NewFlow({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: (graph: Graph) => void
}) {
  const run = useAction()
  const [key, setKey] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  const keyOk = /^[a-z0-9][a-z0-9_-]{1,63}$/.test(key)

  const create = async () => {
    setBusy(true)
    await run(
      async () => {
        const graph = await post<Graph>('/v1/admin/agents/graphs', {
          key,
          name,
          nodes: [],
          edges: [],
        })
        onCreated(graph)
      },
      'Flow created. Add its steps next.',
    )
    setBusy(false)
  }

  return (
    <Modal title="New flow" onClose={onClose}>
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="flow-name">
            Name
          </label>
          <input
            id="flow-name"
            className="input"
            value={name}
            autoFocus
            placeholder="Overnight heart rate watch"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="flow-key">
            Handle
          </label>
          <input
            id="flow-key"
            className="input mono"
            value={key}
            placeholder="overnight_hr_watch"
            onChange={(event) => setKey(event.target.value)}
          />
          {key !== '' && !keyOk && (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Lowercase letters, numbers, dashes and underscores only.
            </p>
          )}
        </div>
        <div className="row row--end">
          <button className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={busy || !keyOk || name.trim() === ''}
            onClick={() => void create()}
          >
            {busy ? 'Creating…' : 'Create flow'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
